import { DownOutlined, FileOutlined, FileSyncOutlined, FileTextOutlined, SolutionOutlined } from '@ant-design/icons';
import { Button, Dropdown, Form, Input, message, Modal, Radio } from 'antd';
import React, { useState} from 'react';
import {
  SendReportCaseV1UpdateInput,
  CasesSendReportV1ChangeStatusContainerSendReportCaseV1FieldsFragment,
  CasesSendReportV1ChangeStatusContainerStaffUserFieldsFragment,
  CasesSendReportV1ChangeStatusContainerFileInfoFieldsFragment
} from '../../../../../../generated';
import { CaseState } from '../../../../../../constants';

const { TextArea } = Input;

interface StateConfig {
  state: string;
  displayName: string;
  icon: React.ReactNode;
}

export interface ChangeStatusProps {
  data: {
    caseData: CasesSendReportV1ChangeStatusContainerSendReportCaseV1FieldsFragment;
    userData: CasesSendReportV1ChangeStatusContainerStaffUserFieldsFragment;
    fileData: CasesSendReportV1ChangeStatusContainerFileInfoFieldsFragment;
  };
  onChangeStatus: (changeStatusInput: SendReportCaseV1UpdateInput) => Promise<void>;
}

export const ChangeStatus: React.FC<ChangeStatusProps> = (props) => {
  const { caseData, userData, fileData } = props.data || {};
  const [changeStatusForm] = Form.useForm();
  const [changeStatusFormLoading, setChangeStatusFormLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [nextState, setNextState] = useState('');
  const [decision, setDecision] = useState(''); // New state for radio button

  const validStatusTransitions = new Map<string, string[]>([
    [CaseState.CASE_WORKER_ASSIGNED, [CaseState.DECISION_RENDERED]],
    [CaseState.REVISION_REQUESTED, [CaseState.CASE_WORKER_ASSIGNED]],
    [CaseState.REVISION_SUBMITTED, [CaseState.DECISION_RENDERED]],
    [CaseState.DECISION_RENDERED, [CaseState.CASE_WORKER_ASSIGNED]]
  ]);

  const stateConfigs: Map<string, StateConfig> = new Map([
    ['draft', { state: 'draft', displayName: 'Draft', icon: <FileOutlined /> }],
    [
      'submitted',
      {
        state: 'submitted',
        displayName: 'Submitted',
        icon: <FileTextOutlined />
      }
    ],
    [
      'caseWorkerAssigned',
      {
        state: 'caseWorkerAssigned',
        displayName: 'Assigned',
        icon: <SolutionOutlined />
      }
    ],
    [
      'revisionRequested',
      {
        state: 'revisionRequested',
        displayName: 'Revision Requested',
        icon: <SolutionOutlined />
      }
    ],
    [
      'revisionSubmitted',
      {
        state: 'revisionSubmitted',
        displayName: 'Revision Submitted',
        icon: <SolutionOutlined />
      }
    ],
    [
      'decisionRendered',
      {
        state: 'decisionRendered',
        displayName: 'Decision Rendered',
        icon: <FileSyncOutlined />
      }
    ]
  ]);

  const buildGenericUpdateInput = (
    id: string,
    newState: string,
    reason: string,
    additionalDetails?: Partial<SendReportCaseV1UpdateInput>
  ): SendReportCaseV1UpdateInput => ({
    id,
    state: { newState, reason },
    ...additionalDetails
  });

  const stateMap = new Map<string, { state: string }>([
    ['draft', { state: 'Draft' }],
    ['submitted', { state: 'Submitted' }],
    ['caseWorkerAssigned', { state: 'Assigned' }],
    ['revisionRequested', { state: 'Revision Requested' }],
    ['revisionSubmitted', { state: 'Revision Submitted' }],
    ['decisionRendered', { state: 'Decision' }]
  ]);

  const menuItems =
  validStatusTransitions.get(caseData?.state)
    ?.map((state: string) => {
      const config = stateConfigs.get(state);
      if (config) {
        return {
          key: config.state,
          icon: config.icon,
          label: config.displayName
        };
      }
      return undefined;
    })
    .filter((item): item is { key: string; icon: React.ReactNode; label: string } => item !== undefined) ?? [];

  const changeStatus = (state: string) => {
    // Check if audit details are available
    if (!areAffirmationsCompleted() && stateMap.get(state)?.state === 'Decision') {
      message.error('Affirmations are not completed. Please complete the affirmations before changing the status.');
      return; // Prevent the modal from opening
    }
    setNextState(state);
    // setDecision('');
    setModalVisible(true);
  };

  const areAffirmationsCompleted = () => {
    const appAudit = caseData?.caseDetails?.applicationReview?.affirmations?.audit;
    return appAudit?.result && appAudit?.completedBy && appAudit?.completedAt
  };

  const canShowDropdown = (): boolean => {
    if (caseData?.state === 'draft' || caseData?.state === 'submitted') {
      return false;
    }
    if (caseData?.state === 'decisionRendered' && userData?.role?.permissions?.sendCasePermissions?.canReopenCases) {
      return true;
    } else if (caseData?.state === 'decisionRendered') {
      return false;
    } else {
      return true;
    }
  };


  return (
    <>
      {canShowDropdown() ? (
        <div style={{ margin: '0', padding: 24 }}>
          <div style={{ marginBottom: '20px' }}>
            <div className="float-right">
              <Dropdown menu={{
                  items: menuItems,
                  onClick: (value) => {
                    changeStatus(value.key);
                  }
                }} 
                disabled={caseData?.state != 'decisionRendered' ? !caseData?.isCurrentUserAssigned : false}>
                <Button>
                  Change Status .. <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
          <Modal
            maskClosable={false}
            title="Change Status"
            open={modalVisible}
            onCancel={() => {
              setModalVisible(false);
            }}
            onOk={() => {
              setModalVisible(false);
            }}
            footer={null}
          >
            <Form
              form={changeStatusForm}
              layout="vertical"
              onFinish={async (values) => {

                setChangeStatusFormLoading(true);


                if (fileData == null && decision == 'completed') {
                  message.error('Please upload or generate the Verification Status Report before completing the case.');
                  setChangeStatusFormLoading(false);
                  return;
                }
                
                console.log('change status form values ', values);
                let additionalInput: Partial<SendReportCaseV1UpdateInput> = {};
                if (nextState === 'decisionRendered') {
                  additionalInput = {
                    caseDetails: {
                      applicationReview: {
                        decision: {
                          result: decision,
                          rejectionReason: decision === 'rejected' ? values.description : undefined
                        }
                      }
                    }
                  };
                }
                const input: SendReportCaseV1UpdateInput = buildGenericUpdateInput(caseData?.id, nextState, values.description, additionalInput);
                await props.onChangeStatus(input);
                setModalVisible(false);
                changeStatusForm.resetFields();
                setChangeStatusFormLoading(false);
              }}
            >
              Current State: <b>{stateMap.get(caseData?.state ?? '')?.state}</b>
              <br />
              <br />
              New State: <b>{stateMap.get(nextState)?.state}</b>
              <br />
              <div>
                <br />
                {(props.data.caseData.state === 'caseWorkerAssigned' || props.data.caseData.state === 'revisionSubmitted') && nextState == 'decisionRendered' && (
                  <Form.Item name="decision" label="Decision">
                    <Radio.Group value={decision} onChange={(e) => setDecision(e.target.value)}>
                      <Radio style={{ display: 'block' }} value="completed">
                        Completed
                      </Radio>
                      <Radio style={{ display: 'block' }} value="rejected">
                        Rejected
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                )}
              </div>
              <Form.Item
                name="description"
                label="Activity Description"
                rules={[
                  {
                    required: true,
                    message: 'Please provide a reason for the status change.'
                  }
                ]}
              >
                <TextArea rows={4} placeholder="Reason for status change." maxLength={2000} />
              </Form.Item>
              <div className={'text-right'}>
                <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                &nbsp;&nbsp;&nbsp;
                <Button type="primary" htmlType="submit" value={'save'} loading={changeStatusFormLoading}>
                  Change Status
                </Button>
              </div>
            </Form>
          </Modal>
        </div>
      ) : null}
    </>
  );
};
